import { Container, Loader, Text, Title } from '@mantine/core';
import { joiResolver, useForm } from '@mantine/form';
import { UseFormReturnType } from '@mantine/form/lib/use-form';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { Check, X } from 'tabler-icons-react';
import { UpdateOnboardFormInitialValues } from '../../data/onboard-form-initival-values.object';
import { BusinessTypes } from '../../enums/business-type';
import { OpenComponent } from '../../enums/misc.enum';
import { removeEmptyPropertiesWithNested } from '../../helpers/filters.helpers';
import {
  apiToIncorporators,
  apiToSignatories,
  createUpdateMerchantOnboardingDataRequest,
  incorporatorsToApi,
  setFormData,
  signatoriesToApi,
  validateUpdateFiles,
} from '../../helpers/merchant-onboarding';
import { filesToSubmit } from '../../helpers/upload-files';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import {
  UpdateOnboardingNonStarterSchema,
  UpdateOnboardingStarterSchema,
} from '../../schemas/update-merchant.schema';
import {
  MerchantFileMetadata,
  RetrieveMerchantResponse,
  UpdateMerchantOnboardingDataRequest,
} from '../../type/admin.types';
import { ErrorResponse } from '../../type/error-response.types';
import { FormSignatory } from '../../type/incorporator-signatory.types';
import { UpdateMerchantOnboardingFormValues } from '../../type/update-merchant-onboarding-form-values.types';
import BackButton from '../buttons/back-button.component';
import UpdateOnboarding from '../update-onboarding/update-onboarding.component';
import { MdrFee } from '../../type/onboard-merchant-request.types';

interface Props {
  merchantId: number;
  setComponentManager: React.Dispatch<React.SetStateAction<OpenComponent>>;
  setRefreshTable: React.Dispatch<React.SetStateAction<boolean>>;
}

function AdminUpdateMerchant(props: Readonly<Props>) {
  const { setComponentManager, merchantId, setRefreshTable } = props;
  const [businessType, setBusinessType] = useState(BusinessTypes.STARTER);
  const [loading, setLoading] = useState(false);
  const axiosPrivate = useAxiosPrivate();
  const [isRetrievedMerchant, setIsRetrievedMerchant] = useState(false);
  const [initialBusinessHandle, setInitialBusinessHandle] = useState('');
  const [merchant, setMerchant] = useState<RetrieveMerchantResponse>();
  const [isFetched, setIsFetched] = useState(false);
  const [mdrFees, setMdrFees] = useState<MdrFee[]>();
  //So that merchantId is not lost when parent component (merchants table) is refreshed.
  const selectedId = merchantId;

  const form = useForm<UpdateMerchantOnboardingFormValues>({
    initialValues: UpdateOnboardFormInitialValues,
    schema:
      businessType === BusinessTypes.STARTER
        ? joiResolver(UpdateOnboardingStarterSchema)
        : joiResolver(UpdateOnboardingNonStarterSchema),
  });

  const endpoint = `/web/onboard/${selectedId}`;

  const deleteEmpty = (
    form: UseFormReturnType<UpdateMerchantOnboardingFormValues>,
    request: UpdateMerchantOnboardingDataRequest
  ) => {
    if (!form.values.facebookUrl || form.values.facebookUrl.trim().length < 1) {
      delete request?.websites?.facebookUrl;
    }
    if (!form.values.twitterUrl || form.values.twitterUrl.trim().length < 1) {
      delete request?.websites?.twitterUrl;
    }
    if (
      !form.values.instagramUrl ||
      form.values.instagramUrl.trim().length < 1
    ) {
      delete request?.websites?.instagramUrl;
    }
    if (!form.values.tiktokUrl || form.values.tiktokUrl.trim().length < 1) {
      delete request?.websites?.tiktokUrl;
    }
    if (form.values.businessTypeId === BusinessTypes.STARTER) {
      delete request.incorporators;
      delete request.address.registered;
      delete request.legalName;
    }

    if (!form.values.mainOfficeBldgNameNo) {
      delete request.address.office.buildingNameAndNumber;
    }

    if (!form.values.registeredBldgNameNo) {
      if (request.address.registered !== undefined) {
        delete request.address.registered.buildingNameAndNumber;
      }
    }

    request.incorporators?.forEach((incorporator) => {
      if (!incorporator.address.buildingNameAndNumber) {
        delete incorporator.address.buildingNameAndNumber;
      }
    });

    if (!form.values.currency) {
      request.currencyId = null;
    }
  };

  const fetchMerchant = async () => {
    setLoading(true);
    try {
      const result = await axiosPrivate.get(endpoint);
      const resData: RetrieveMerchantResponse = result.data;

      setMdrFees(resData.mdrFees);

      setBusinessType(resData.businessTypeId);
      const restructuredIncorporators = apiToIncorporators(
        resData.incorporators
      );

      let restructuredSignatories: FormSignatory[] = [];
      if (resData.signatories) {
        restructuredSignatories = apiToSignatories(resData.signatories);
      }

      setMerchant(resData);

      setFormData(
        form,
        resData,
        restructuredIncorporators,
        restructuredSignatories
      );
      setInitialBusinessHandle(resData.businessHandle);
      setIsRetrievedMerchant(true);
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      setIsRetrievedMerchant(false);
      showNotification({
        id: 'failed-to-retrieve-merchant',
        message: error.error || 'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setLoading(false);
      setIsFetched(true);
    }
  };
  useEffect(() => {
    fetchMerchant();
  }, []);
  const handleSubmit = async (values: typeof form.values) => {
    setLoading(true);

    const validFiles = validateUpdateFiles(form);
    if (!validFiles) {
      setLoading(false);
      return;
    }

    const restructuredIncorporators = incorporatorsToApi(form);
    const restructuredSignatories = signatoriesToApi(form);

    try {
      const filesResult: MerchantFileMetadata[] = await filesToSubmit(
        form,
        merchant,
        values,
        selectedId
      );

      const request: UpdateMerchantOnboardingDataRequest =
        createUpdateMerchantOnboardingDataRequest(
          restructuredIncorporators,
          restructuredSignatories,
          values
        );

      deleteEmpty(form, request);
      removeEmptyPropertiesWithNested(request);

      request.files = filesResult;
      request.mdrFees = mdrFees;

      await axiosPrivate.put(endpoint, request);
      showNotification({
        id: 'successfully-updated-merchant',
        message: 'Successfully updated merchant',
        icon: <Check size={16} />,
        color: 'teal',
        autoClose: 6000,
      });
      setRefreshTable((prev) => !prev);
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-update-merchant',
        message: error.error || 'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      fluid
      px={0}
      style={{
        margin: 0,
      }}
    >
      <Container fluid sx={{ padding: 0 }}>
        <BackButton
          handleClick={() => {
            setComponentManager(OpenComponent.HOME);
          }}
        />
        <Title order={2} my="lg">
          Update Merchant
        </Title>
        {!isFetched && <Loader />}
        {isFetched && isRetrievedMerchant && (
          <UpdateOnboarding
            form={form}
            initialBusinessHandle={initialBusinessHandle}
            handleSubmit={handleSubmit}
            isNonAdminInterface={false}
            loading={loading}
            mdrFees={mdrFees}
            setMdrFees={setMdrFees}
            parentTypeId={merchant?.parentTypeId}
          />
        )}
        {isFetched && !isRetrievedMerchant && <Text>Merchant not found</Text>}
      </Container>
    </Container>
  );
}

export default AdminUpdateMerchant;
